import { useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { MailCheck, Users } from "lucide-react";

import { passwordStrength, registrationErrors, newPasswordErrors, isEmail, hasErrors } from "@/lib/memberForms";
import { PublicShell } from "../components/PublicShell";
import { BlockedNotice } from "../components/forms";
import { Unavailable } from "../components/kit";
import { V3Columns, V3Field, V3PasswordField, V3Check, V3Actions, PhotoAside, StatusBanner, DetailRow, DetailList, ShowPasswordToggle, V3_PRIMARY, V3_SECONDARY } from "../components/v3";
import { LEGAL_DOCS, isReleased, registrationGate } from "@/lib/legalDocs";

const link = "font-bold text-v3-magenta underline-offset-4 hover:underline";
const photo = <PhotoAside icon={Users} />;

export function StrengthMeter({ password, testid = "password-strength" }) {
  const s = passwordStrength(password);
  return (
    <div data-testid={testid} data-level={s.level} className="-mt-[6px] mb-[17px]">
      <div className="flex gap-1" aria-hidden="true">
        {[1, 2, 3, 4].map((i) => <span key={i} className={`h-1.5 flex-1 rounded-full ${i <= s.level ? (s.level < 2 ? "bg-red-700" : "bg-v3-magenta") : "bg-v3-line"}`} />)}
      </div>
      <p className="mt-1 text-[13px] text-v3-muted" aria-live="polite">Passwortstärke: <strong>{s.label}</strong>. Mindestens 10 Zeichen – ein längerer Satz ist sicherer als komplizierte Zeichen.</p>
    </div>
  );
}

// M02 / M33 – registration opens only when released legal texts are stored in this project (lib/legalDocs.js).
export function Register() {
  const gate = registrationGate();
  return gate.open ? <RegisterForm consent={gate.consent} /> : <RegisterLocked missing={gate.missing} />;
}

function RegisterLocked({ missing }) {
  return (
    <PublicShell title="Konto erstellen" testid="public-register" intro="Erstelle dein Konto. Persönliche Unterstützungsvorlieben bleiben freiwillig.">
      <V3Columns layout="form" aside={photo} main={
        <div data-m="M02 M33" data-testid="register-locked">
          <div className="mb-[17px]"><Unavailable testid="register-unavailable">Die Registrierung ist im Moment nicht möglich. Für ein neues Konto stimmst du den verbindlichen Nutzungsregeln und Datenschutzhinweisen zu – diese Fassungen sind in der App noch nicht hinterlegt. Es wird nichts gesendet und kein Konto angelegt.</Unavailable></div>
          <DetailList>
            {missing.map((k) => (
              <DetailRow key={k} to={`/rechtliches/${k}`} testid={`register-legal-${k}`} label={LEGAL_DOCS[k].title} value="Noch nicht hinterlegt" />
            ))}
          </DetailList>
          <V3Actions>
            <Link to="/login" className={V3_PRIMARY} data-testid="register-to-login">Zur Anmeldung</Link>
          </V3Actions>
        </div>
      } />
    </PublicShell>
  );
}

const versionOf = (consent, doc) => consent.find((c) => c.doc === doc).version;

function RegisterForm({ consent }) {
  const [f, setF] = useState({ firstName: "", email: "", password: "", terms: false, news: false });
  const [errors, setErrors] = useState({});
  const [blocked, setBlocked] = useState(false);
  const set = (k) => (v) => { setF((p) => ({ ...p, [k]: v })); setBlocked(false); };
  const submit = (e) => { e.preventDefault(); const er = registrationErrors(f); setErrors(er); setBlocked(!hasErrors(er)); };
  const legalLink = (doc, label) => <Link to={`/rechtliches/${doc}`} target="_blank" rel="noopener" className={link} data-testid={`register-legal-${doc}`}>{label}<span className="sr-only"> (Fassung {versionOf(consent, doc)}) – öffnet in neuem Tab</span></Link>;
  return (
    <PublicShell title="Konto erstellen" testid="public-register" intro="Erstelle dein Konto ohne Diagnose- oder Nachweispflicht.">
      <V3Columns layout="form" aside={photo} main={
        <form onSubmit={submit} noValidate data-m="M02 M33" data-testid="register-form">
          <V3Field id="reg-first-name" label="Anzeigename" value={f.firstName} onChange={(e) => set("firstName")(e.target.value)} autoComplete="given-name" error={errors.firstName} placeholder="Noch nicht eingegeben" />
          <V3Field id="reg-email" type="email" label="E-Mail-Adresse" value={f.email} onChange={(e) => set("email")(e.target.value)} autoComplete="email" inputMode="email" error={errors.email} placeholder="Noch nicht eingegeben" />
          <V3PasswordField id="reg-password" label="Passwort" value={f.password} onChange={set("password")} error={errors.password} />
          {f.password && <StrengthMeter password={f.password} />}
          <div className="mt-[4px]">
            <V3Check id="reg-terms" checked={f.terms} onChange={set("terms")} error={errors.terms}>
              Ich habe die {legalLink("nutzungsregeln", "Nutzungsregeln")} und {legalLink("datenschutz", "Datenschutzhinweise")} gelesen.
            </V3Check>
            <V3Check id="reg-news" checked={f.news} onChange={set("news")}>Ich möchte freiwillige Neuigkeiten per E-Mail erhalten.</V3Check>
          </div>
          {blocked && <BlockedNotice what="gesendet" testid="register-blocked">Die Registrierung ist im gemeinsamen VISIBILIS-System noch nicht eingerichtet. Es wurde kein Konto angelegt und keine E-Mail verschickt.</BlockedNotice>}
          <V3Actions>
            <button type="submit" className={V3_PRIMARY} data-testid="register-submit">Konto erstellen</button>
            <Link to="/login" className={V3_SECONDARY} data-testid="register-to-login">Zurück</Link>
          </V3Actions>
        </form>
      } />
    </PublicShell>
  );
}

const ACCESS_TEXT = "Bitte bestätige deine E-Mail-Adresse, um die Einrichtung fortzusetzen.";

// M34 – confirmation step; no e-mail can have been sent while registration is blocked.
export function VerifyEmail() {
  const { state } = useLocation();
  const [blocked, setBlocked] = useState(false);
  const address = isEmail(state?.email || "") ? state.email : "Nicht angegeben";
  return (
    <PublicShell title="E-Mail bestätigen" testid="public-verify-email" intro={ACCESS_TEXT}>
      <V3Columns layout="form" aside={photo} main={
        <div data-m="M34">
          <StatusBanner title="Persönlichen Zugang bestätigen" text={ACCESS_TEXT} testid="verify-banner" />
          <DetailList>
            <DetailRow label="E-Mail-Adresse" value={address} testid="verify-address" />
            <DetailRow label="Postfach" value="Bestätigungsnachricht öffnen" />
            <DetailRow label="Adresse korrigieren" value="Bei einem Tippfehler ändern" to="/registrieren" testid="verify-change-address" />
            <DetailRow label="Neue Nachricht" value="Bewusst erneut anfordern" to="/email-hilfe" testid="verify-help" />
          </DetailList>
          <div className="mt-[17px]"><Unavailable testid="verify-unavailable">Da die Registrierung noch nicht verfügbar ist, wurde bisher keine Bestätigungs-E-Mail versendet. Codes musst du nirgends abtippen – der Link in der E-Mail genügt.</Unavailable></div>
          {blocked && <div className="mt-[17px]"><BlockedNotice what="gesendet" testid="verify-resend-blocked">Der erneute Versand ist noch nicht eingerichtet.</BlockedNotice></div>}
          <V3Actions>
            <button type="button" onClick={() => setBlocked(true)} className={V3_PRIMARY} data-testid="verify-resend">E-Mail erneut senden</button>
            <Link to="/login" className={V3_SECONDARY} data-testid="verify-back">Zurück</Link>
          </V3Actions>
        </div>
      } />
    </PublicShell>
  );
}

const FORGOT_TEXT = "Fordere einen Link an. Die Rückmeldung bleibt für alle Adressen gleich.";

// M30 – request a reset link; no neutral success is claimed because nothing is sent.
export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [blocked, setBlocked] = useState(false);
  const submit = (e) => { e.preventDefault(); const ok = isEmail(email); setError(ok ? "" : "Bitte gib eine gültige E-Mail-Adresse ein."); setBlocked(ok); };
  return (
    <PublicShell title="Passwort vergessen?" testid="public-forgot" intro={FORGOT_TEXT}>
      <V3Columns layout="form" aside={photo} main={
        <form onSubmit={submit} noValidate data-m="M30" data-testid="forgot-form">
          <StatusBanner title="Persönlichen Zugang bestätigen" text={FORGOT_TEXT} />
          <V3Field id="forgot-email" type="email" label="E-Mail-Adresse" value={email} onChange={(e) => { setEmail(e.target.value); setBlocked(false); }} autoComplete="email" error={error} placeholder="Noch nicht eingegeben" />
          <StatusBanner title="Neutrale Rückmeldung" text="Falls ein Konto zu dieser Adresse besteht, erhältst du einen Link." testid="forgot-neutral" />
          {blocked && <BlockedNotice what="gesendet" testid="forgot-blocked">Das Zurücksetzen per E-Mail ist noch nicht eingerichtet. Es wurde keine E-Mail verschickt.</BlockedNotice>}
          <V3Actions>
            <button type="submit" className={V3_PRIMARY} data-testid="forgot-submit">Link anfordern</button>
            <Link to="/login" className={V3_SECONDARY} data-testid="forgot-back">Zurück</Link>
          </V3Actions>
        </form>
      } />
    </PublicShell>
  );
}

// M31 – new password after the e-mail link; no token handling without a Core endpoint.
export function ResetPassword() {
  const [pw, setPw] = useState("");
  const [repeat, setRepeat] = useState("");
  const [show, setShow] = useState(false);
  const [errors, setErrors] = useState({});
  const [blocked, setBlocked] = useState(false);
  const submit = (e) => { e.preventDefault(); const er = newPasswordErrors(pw, repeat); setErrors(er); setBlocked(!hasErrors(er)); };
  return (
    <PublicShell title="Neues Passwort eingeben" testid="public-reset" intro="Wähle ein neues Passwort und bestätige es ein zweites Mal.">
      <V3Columns layout="form" aside={photo} main={
        <form onSubmit={submit} noValidate data-m="M31" data-testid="reset-form">
          <V3PasswordField id="reset-password" label="Neues Passwort" value={pw} onChange={(v) => { setPw(v); setBlocked(false); }} error={errors.password} show={show} onShowChange={setShow} withShowRow={false} />
          {pw && <StrengthMeter password={pw} testid="reset-strength" />}
          <V3PasswordField id="reset-repeat" label="Passwort wiederholen" value={repeat} onChange={(v) => { setRepeat(v); setBlocked(false); }} error={errors.repeat} show={show} onShowChange={setShow} withShowRow={false} />
          <ShowPasswordToggle show={show} onChange={setShow} controls="reset-password reset-repeat" testid="reset-show" />
          {blocked && <div className="mt-[17px]"><BlockedNotice what="gespeichert" testid="reset-blocked">Das Ändern des Passworts über einen E-Mail-Link ist noch nicht eingerichtet.</BlockedNotice></div>}
          <V3Actions>
            <button type="submit" className={V3_PRIMARY} data-testid="reset-submit">Passwort speichern</button>
            <Link to="/login" className={V3_SECONDARY} data-testid="reset-cancel">Abbrechen</Link>
          </V3Actions>
        </form>
      } />
    </PublicShell>
  );
}

// Binding texts come only from lib/legalDocs.js; nothing is invented here.
export function LegalPage() {
  const { doc } = useParams();
  const d = LEGAL_DOCS[doc];
  const released = isReleased(d);
  return (
    <PublicShell title={d ? d.title : "Dokument nicht gefunden"} testid="public-legal">
      <div className="max-w-[744px] space-y-4">
        {released ? <>
          <p className="text-[14px] font-bold text-v3-ink" data-testid="legal-version">Fassung {d.version} · veröffentlicht am {d.publishedAt}</p>
          <div className="whitespace-pre-line text-[15px] text-v3-ink" data-testid="legal-body">{d.body}</div>
        </> : <p className="flex gap-2 text-[15px] text-v3-ink" data-testid="legal-status"><MailCheck className="mt-0.5 h-5 w-5 shrink-0 text-v3-magenta" aria-hidden="true" /> {d ? "Die verbindliche Fassung wird von VISIBILIS bereitgestellt und ist in der App noch nicht hinterlegt." : "Unter dieser Adresse gibt es kein Dokument. Verfügbar sind die Nutzungsregeln und die Datenschutzhinweise."}</p>}
        <V3Actions><Link to="/registrieren" className={V3_SECONDARY} data-testid="legal-back-register">Zurück</Link></V3Actions>
      </div>
    </PublicShell>
  );
}
